import { useState, useMemo, useCallback, useEffect, useRef, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { createNotification } from "@/lib/createNotification";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Receipt, Maximize2, Minimize2, ChevronRight, ChevronDown, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { generateBillNumber } from "@/lib/naming";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQBStatus } from "@/hooks/useQBStatus";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtRate = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);
const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wo: any;
  woLines: any[];
  projectId: string;
  onCreated: () => void;
}

export const WOCreateBillDialog = ({ open, onOpenChange, wo, woLines, projectId, onCreated }: Props) => {
  const { connected: qbConnected } = useQBStatus();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // partial qty: map of wol.id → billing qty (used for quantity-based lines)
  const [billingQtys, setBillingQtys] = useState<Record<string, number>>({});
  // partial amount: map of wol.id → billing amount (used for lump-sum lines with qty=1)
  const [billingAmounts, setBillingAmounts] = useState<Record<string, number>>({});
  const [billDate, setBillDate] = useState(new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [qbBusy, setQbBusy] = useState(false);
  const [qbDraftCreated, setQbDraftCreated] = useState(false);
  const [qbDraftUrl, setQbDraftUrl] = useState<string | null>(null);
  const qbDraftSelectionRef = useRef<string>("");
  const [expanded, setExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // If the user changes the selected lines after creating a QB draft, reset
  // so the next click creates a fresh draft matching the new selection.
  useEffect(() => {
    if (!qbDraftCreated) return;
    const current = Array.from(selectedIds).sort().join(",");
    if (current !== qbDraftSelectionRef.current) {
      setQbDraftCreated(false);
      setQbDraftUrl(null);
    }
  }, [selectedIds, qbDraftCreated]);



  const getScheduledQty = useCallback((wol: any) => Number(wol.quantity ?? 0), []);
  const getRate = useCallback((wol: any) => Number(wol.contract_rate ?? 0), []);
  const getScheduledAmount = useCallback((wol: any) => getScheduledQty(wol) * getRate(wol), [getRate, getScheduledQty]);
  const getAlreadyBilledAmount = useCallback((wol: any) => Number(wol.billed_amount ?? 0), []);
  const getRemainingAmount = useCallback((wol: any) => {
    const remaining = getScheduledAmount(wol) - getAlreadyBilledAmount(wol);
    return remaining > 0 ? r2(remaining) : 0;
  }, [getAlreadyBilledAmount, getScheduledAmount]);
  const getRemainingQty = useCallback((wol: any) => {
    const rate = getRate(wol);
    const fullQty = getScheduledQty(wol);
    if (rate <= 0) return fullQty;
    return Math.min(fullQty, getRemainingAmount(wol) / rate);
  }, [getRate, getRemainingAmount, getScheduledQty]);
  const usesAmountInput = useCallback((wol: any) => {
    const fullQty = getScheduledQty(wol);
    const remainingAmount = getRemainingAmount(wol);
    return fullQty === 1 && remainingAmount > 0 && remainingAmount < getScheduledAmount(wol);
  }, [getRemainingAmount, getScheduledAmount, getScheduledQty]);

  // Only show lines that still have money left to bill.
  const billableLines = useMemo(() => {
    return woLines.filter((wol: any) =>
      wol.work_order_id === wo.id &&
      ["pending", "complete"].includes(wol.status) &&
      getRemainingAmount(wol) > 0
    );
  }, [woLines, wo.id, getRemainingAmount]);

  // Consolidate by sov_line_id for display
  const consolidatedGroups = useMemo(() => {
    const groups: Record<string, { sovLineId: string; description: string; unit: string; lines: any[] }> = {};
    billableLines.forEach((wol: any) => {
      const key = wol.sov_line_id ?? wol.id;
      if (!groups[key]) {
        groups[key] = {
          sovLineId: key,
          description: wol.description,
          unit: wol.unit ?? "",
          lines: [],
        };
      }
      groups[key].lines.push(wol);
    });
    return Object.values(groups);
  }, [billableLines]);

  const toggleLine = useCallback((wol: any) => {
    const wolId = wol.id as string;
    const useAmount = usesAmountInput(wol);
    // Check current state outside setter to avoid calling a setter inside a setter callback
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(wolId)) next.delete(wolId);
      else next.add(wolId);
      return next;
    });
    setBillingQtys(prev => {
      if (useAmount) {
        const { [wolId]: _removed, ...rest } = prev;
        return rest;
      }
      if (prev[wolId] !== undefined) {
        const { [wolId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [wolId]: getRemainingQty(wol) };
    });
    setBillingAmounts(prev => {
      if (!useAmount) {
        const { [wolId]: _removed, ...rest } = prev;
        return rest;
      }
      if (prev[wolId] !== undefined) {
        const { [wolId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [wolId]: getRemainingAmount(wol) };
    });
  }, [getRemainingAmount, getRemainingQty, usesAmountInput]);

  const updateBillingQty = useCallback((wolId: string, maxQty: number, val: string) => {
    const parsed = parseFloat(val);
    if (isNaN(parsed) || parsed <= 0) return;
    const clamped = Math.min(parsed, maxQty);
    setBillingQtys(q => ({ ...q, [wolId]: clamped }));
  }, []);

  const updateBillingAmount = useCallback((wolId: string, maxAmount: number, val: string) => {
    const parsed = parseFloat(val);
    if (isNaN(parsed) || parsed <= 0) return;
    const clamped = Math.min(parsed, maxAmount);
    setBillingAmounts(q => ({ ...q, [wolId]: r2(clamped) }));
  }, []);

  const toggleGroup = (groupKey: string, lines: any[]) => {
    const allSelected = lines.every(l => selectedIds.has(l.id));
    const newIds = new Set(selectedIds);
    const newQtys = { ...billingQtys };
    lines.forEach(l => {
      if (allSelected) {
        newIds.delete(l.id);
        delete newQtys[l.id];
      } else {
        newIds.add(l.id);
          if (!usesAmountInput(l)) {
            newQtys[l.id] = getRemainingQty(l);
          }
      }
    });
    setSelectedIds(newIds);
    setBillingQtys(newQtys);
      setBillingAmounts(prev => {
        const next = { ...prev };
        lines.forEach(l => {
          if (allSelected || !usesAmountInput(l)) {
            delete next[l.id];
          } else {
            next[l.id] = getRemainingAmount(l);
          }
        });
        return next;
      });
    // Auto-expand the group when selecting so user can see/adjust children
    if (!allSelected) {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        next.add(groupKey);
        return next;
      });
    }
  };

  const toggleGroupExpansion = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === billableLines.length) {
      setSelectedIds(new Set());
      setBillingQtys({});
      setBillingAmounts({});
    } else {
      const newSet = new Set(billableLines.map(l => l.id));
      const newQtys: Record<string, number> = {};
      const newAmounts: Record<string, number> = {};
      billableLines.forEach(l => {
        if (usesAmountInput(l)) newAmounts[l.id] = getRemainingAmount(l);
        else newQtys[l.id] = getRemainingQty(l);
      });
      setSelectedIds(newSet);
      setBillingQtys(newQtys);
      setBillingAmounts(newAmounts);
    }
  };

  const selectedLines = billableLines.filter(l => selectedIds.has(l.id));

  // Use billing qty (partial) where available, otherwise full qty
  const getBillingQty = (wol: any) => billingQtys[wol.id] ?? getRemainingQty(wol);
  const getBillingAmount = (wol: any) => billingAmounts[wol.id] ?? getRemainingAmount(wol);
  const getLineBillTotal = (wol: any) => usesAmountInput(wol) ? getBillingAmount(wol) : r2(getBillingQty(wol) * getRate(wol));


  const subtotal = r2(selectedLines.reduce((s, wol) => s + getLineBillTotal(wol), 0));

  const retainagePct = Number(wo.retainage_pct ?? 10);
  const retainageAmount = r2(subtotal * (retainagePct / 100));
  const total = r2(subtotal - retainageAmount);

  const handleOpenInQB = async () => {
    if (selectedIds.size === 0) {
      window.open("https://app.qbo.intuit.com/app/bill?newTxn=true", "_blank");
      return;
    }
    // If we already created a draft for this exact selection, just reopen it.
    if (qbDraftUrl && qbDraftCreated) {
      window.open(qbDraftUrl, "_blank");
      return;
    }
    setQbBusy(true);
    try {
      const lines = selectedLines.map(wol => ({
        description: `${wol.description}${wol.unit_schedule?.unit_number ? ` — Unit ${wol.unit_schedule.unit_number}` : ""}`,
        quantity: usesAmountInput(wol) ? 1 : getBillingQty(wol),
        rate: usesAmountInput(wol) ? getBillingAmount(wol) : Number(wol.contract_rate),
      }));
      const { data, error } = await supabase.functions.invoke("qb-create-draft-bill", {
        body: {
          vendor_id: wo.contractor_id ?? null,
          project_id: projectId,
          wo_number: wo.number,
          lines,
          bill_date: billDate,
          due_date: dueDate || null,
          memo: description || `Draft from ${wo.number}`,
        },
      });

      // supabase.functions.invoke swallows the response body on non-2xx — extract it
      let errorBody: any = null;
      if (error && (error as any).context) {
        try { errorBody = await (error as any).context.json(); } catch { /* ignore */ }
      }

      if (error || !data?.redirect_url) {
        const code = errorBody?.code ?? (data as any)?.code ?? null;
        const msg = errorBody?.error ?? (data as any)?.error ?? error?.message ?? "No redirect URL returned";
        const err: any = new Error(msg);
        err.code = code;
        throw err;
      }
      window.open(data.redirect_url, "_blank");
      setQbDraftUrl(data.redirect_url as string);
      setQbDraftCreated(true);
      qbDraftSelectionRef.current = Array.from(selectedIds).sort().join(",");
      toast.success("Draft bill created in QuickBooks — save it there to sync back to Command");
    } catch (err: any) {
      if (err?.code === "QB_REAUTH_REQUIRED" || /reconnect|expired|token/i.test(err?.message ?? "")) {
        toast.error("QuickBooks connection expired. Reconnect QuickBooks in Settings → Integrations, then try again.", { duration: 8000 });
      } else if (err?.code === "QB_DELETED_VENDOR" || err?.code === "NO_QB_VENDOR" || err?.code === "NO_VENDOR" || err?.code === "NO_EXPENSE_ACCOUNT") {
        toast.error(err.message, { duration: 10000 });
      } else {
        toast.error(err?.message ? `QuickBooks: ${err.message}` : "Could not pre-fill QB bill — opening blank form instead", { duration: 8000 });
        window.open("https://app.qbo.intuit.com/app/bill?newTxn=true", "_blank");
      }
    } finally {
      setQbBusy(false);
    }
  };

  const handleCreateBill = async () => {
    if (selectedIds.size === 0) return toast.error("Select at least one line to bill");
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const userId = (await supabase.auth.getUser()).data.user?.id;

      const billNumber = await generateBillNumber(wo.id, wo.number);

      const lines = selectedLines.map(wol => {
        const useAmount = usesAmountInput(wol);
        const billingQty = useAmount ? 1 : getBillingQty(wol);
        const unitPrice = useAmount ? getBillingAmount(wol) : Number(wol.contract_rate);
        const lineTotal = useAmount ? getBillingAmount(wol) : r2(billingQty * Number(wol.contract_rate));
        return {
          description: `${wol.description}${wol.unit_schedule?.unit_number ? ` — Unit ${wol.unit_schedule.unit_number}` : ""}`,
          quantity: billingQty,
          unit_price: unitPrice,
          line_total: lineTotal,
          sov_line_id: wol.sov_line_id ?? null,
          work_order_line_id: wol.id,
        };
      });

      // Use RPC so bill + lines + WO line updates are one atomic transaction
      // with contract-lock triggers disabled (billing ≠ contract modification).
      const { data: result, error } = await supabase.rpc("create_bill_from_wo" as any, {
        p_company_id:    companyId,
        p_project_id:    projectId,
        p_work_order_id: wo.id,
        p_vendor_id:     wo.contractor_id ?? null,
        p_bill_number:   billNumber,
        p_bill_date:     billDate,
        p_due_date:      dueDate || null,
        p_description:   description || `Bill from ${wo.number}`,
        p_subtotal:      subtotal,
        p_retainage_pct: retainagePct,
        p_retainage_amt: retainageAmount,
        p_total:         total,
        p_created_by:    userId ?? null,
        p_lines:         lines,
      });
      if (error) throw error;

      const billId = (result as any).id as string;

      // Audit log (non-blocking)
      try {
        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "bills",
          record_id: billId,
          action: "bill_created",
          new_status: "draft",
          changed_by: userId,
          metadata: { wo_number: wo.number, bill_number: billNumber, lines_count: selectedIds.size, subtotal, retainage: retainageAmount, total },
        } as any);
      } catch { /* non-blocking */ }

      toast.success(`${billNumber} created — ${selectedIds.size} lines | Total: ${fmt(total)}`);

      createNotification({
        entity_type: "bill", entity_id: billId, entity_label: billNumber,
        project_id: projectId, type: "bill_created", title: "New Bill Created",
        message: `${billNumber} — ${fmt(total)} (${selectedIds.size} line items)`,
        priority: "normal",
        action_url: `/controller/projects/${projectId}?tab=work_orders`,
      });

      setSelectedIds(new Set());
      setBillingQtys({});
      setBillingAmounts({});
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setQbDraftCreated(false); onOpenChange(v); }}>
      <DialogContent className={cn(
        "transition-all duration-200 flex flex-col p-0",
        expanded ? "max-w-[95vw] h-[95vh]" : "max-w-4xl max-h-[90vh]"
      )}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 flex-row items-center justify-between">
          <div>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" /> Create Bill — {wo.number}
            </DialogTitle>
            <DialogDescription>
              Select lines to bill from this work order. {billableLines.length} billable line{billableLines.length !== 1 ? "s" : ""} available.
            </DialogDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setExpanded(e => !e)} className="shrink-0">
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Bill metadata */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Bill Date</label>
              <Input type="date" value={billDate} onChange={e => setBillDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Due Date (optional)</label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Description (optional)</label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={`Bill from ${wo.number}`}
              rows={2}
            />
          </div>

          {/* Line selection */}
          {billableLines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No billable lines — all lines are already billed, paid, or voided.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedIds.size === billableLines.length && billableLines.length > 0}
                  onCheckedChange={selectAll}
                  onClick={e => e.stopPropagation()}
                />
                <span className="text-sm font-medium cursor-pointer" onClick={selectAll}>
                  Select All ({billableLines.length})
                </span>
              </div>

              <div className="border rounded-md overflow-y-auto" style={{ maxHeight: expanded ? undefined : 350 }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">WO Qty</TableHead>
                      <TableHead className="text-right w-28">Bill Qty / Amt</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Bill Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consolidatedGroups.map(group => {
                      const groupSelected = group.lines.every(l => selectedIds.has(l.id));
                      const groupPartial = group.lines.some(l => selectedIds.has(l.id)) && !groupSelected;

                      if (group.lines.length === 1) {
                        const wol = group.lines[0];
                        const fullQty = Number(wol.quantity);
                        const remainingQty = getRemainingQty(wol);
                        const rate = Number(wol.contract_rate);
                        const isSelected = selectedIds.has(wol.id);
                        const billQty = getBillingQty(wol);
                        const billAmount = getBillingAmount(wol);
                        const showAmountInput = usesAmountInput(wol);
                        return (
                          <TableRow
                            key={wol.id}
                            className={cn("cursor-pointer", isSelected && "bg-primary/5")}
                            onClick={() => toggleLine(wol)}
                          >
                            <TableCell>
                              {/* stopPropagation prevents row onClick from double-firing */}
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleLine(wol)}
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="text-sm">
                              {wol.description}
                              {wol.unit_schedule?.unit_number && (
                                <span className="text-muted-foreground ml-1">· Unit {wol.unit_schedule.unit_number}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">{fullQty.toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              {isSelected ? (
                                showAmountInput ? (
                                  <Input
                                    type="number"
                                    min={0.01}
                                    max={getRemainingAmount(wol)}
                                    step="0.01"
                                    value={billAmount}
                                    className="w-24 h-7 text-right text-sm"
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => updateBillingAmount(wol.id, getRemainingAmount(wol), e.target.value)}
                                  />
                                ) : (
                                  <Input
                                    type="number"
                                    min={0.01}
                                    max={remainingQty}
                                    step="any"
                                    value={billQty}
                                    className="w-24 h-7 text-right text-sm"
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => updateBillingQty(wol.id, remainingQty, e.target.value)}
                                  />
                                )
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                              {isSelected && (
                                <div className="text-[10px] text-amber-600 mt-0.5">partial</div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{wol.unit ?? ""}</TableCell>
                            <TableCell className="text-right text-sm">{fmtRate(rate)}</TableCell>
                            <TableCell className="text-right font-medium text-sm">
                              {fmt(isSelected ? getLineBillTotal(wol) : getRemainingAmount(wol))}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">{wol.status}</Badge>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      // Multi-line group — expandable parent with child rows
                      const totalQty = group.lines.reduce((s: number, l: any) => s + Number(l.quantity), 0);
                      const rate = Number(group.lines[0].contract_rate);
                      const selectedChildren = group.lines.filter((l: any) => selectedIds.has(l.id));
                      const groupTotal = group.lines.reduce((s: number, l: any) => {
                        return s + (selectedIds.has(l.id) ? getLineBillTotal(l) : getRemainingAmount(l));
                      }, 0);
                      const isExpanded = expandedGroups.has(group.sovLineId);

                      return (
                        <Fragment key={group.sovLineId}>
                          <TableRow
                            className={cn("cursor-pointer bg-muted/40 hover:bg-muted/60", groupSelected && "bg-primary/10")}
                            onClick={() => toggleGroupExpansion(group.sovLineId)}
                          >
                            <TableCell className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleGroupExpansion(group.sovLineId); }}
                                className="text-muted-foreground hover:text-foreground"
                                aria-label={isExpanded ? "Collapse" : "Expand"}
                              >
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                              <Checkbox
                                checked={groupSelected}
                                // @ts-ignore
                                indeterminate={groupPartial}
                                onCheckedChange={() => toggleGroup(group.sovLineId, group.lines)}
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {group.description}
                              <span className="text-muted-foreground ml-1 font-normal">· {group.lines.length} sub-lines{selectedChildren.length > 0 ? ` · ${selectedChildren.length} selected` : ""}</span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">{totalQty.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-sm text-muted-foreground">—</TableCell>
                            <TableCell className="text-sm">{group.unit}</TableCell>
                            <TableCell className="text-right text-sm">{fmtRate(rate)}</TableCell>
                            <TableCell className="text-right font-medium text-sm">{fmt(groupTotal)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">{group.lines[0].status}</Badge>
                            </TableCell>
                          </TableRow>

                          {isExpanded && group.lines.map((wol: any) => {
                            const fullQty = Number(wol.quantity);
                            const remainingQty = getRemainingQty(wol);
                            const childRate = Number(wol.contract_rate);
                            const isSelected = selectedIds.has(wol.id);
                            const billQty = getBillingQty(wol);
                            const billAmount = getBillingAmount(wol);
                            const showAmountInput = usesAmountInput(wol);
                            const unitLabel = wol.unit_schedule?.unit_number
                              ? `Unit ${wol.unit_schedule.unit_number}`
                              : wol.cost_type
                                ? String(wol.cost_type).charAt(0).toUpperCase() + String(wol.cost_type).slice(1)
                                : null;
                            return (
                              <TableRow
                                key={wol.id}
                                className={cn("cursor-pointer", isSelected && "bg-primary/5")}
                                onClick={() => toggleLine(wol)}
                              >
                                <TableCell className="pl-8">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleLine(wol)}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </TableCell>
                                <TableCell className="text-sm pl-8">
                                  {unitLabel && <span className="text-muted-foreground mr-1">{unitLabel} ·</span>}
                                  <span>{wol.description}</span>
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-muted-foreground">{fullQty.toLocaleString()}</TableCell>
                                <TableCell className="text-right">
                                  {isSelected ? (
                                    showAmountInput ? (
                                      <Input
                                        type="number"
                                        min={0.01}
                                        max={getRemainingAmount(wol)}
                                        step="0.01"
                                        value={billAmount}
                                        className="w-24 h-7 text-right text-sm"
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => updateBillingAmount(wol.id, getRemainingAmount(wol), e.target.value)}
                                      />
                                    ) : (
                                      <Input
                                        type="number"
                                        min={0.01}
                                        max={remainingQty}
                                        step="any"
                                        value={billQty}
                                        className="w-24 h-7 text-right text-sm"
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => updateBillingQty(wol.id, remainingQty, e.target.value)}
                                      />
                                    )
                                  ) : (
                                    <span className="text-sm text-muted-foreground">—</span>
                                  )}
                                  {isSelected && (
                                    <div className="text-[10px] text-amber-600 mt-0.5">partial</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">{wol.unit ?? ""}</TableCell>
                                <TableCell className="text-right text-sm">{fmtRate(childRate)}</TableCell>
                                <TableCell className="text-right font-medium text-sm">
                                  {fmt(isSelected ? getLineBillTotal(wol) : getRemainingAmount(wol))}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-[10px]">{wol.status}</Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {/* Summary */}
          {selectedIds.size > 0 && (
            <div className="border rounded-md p-3 space-y-1 bg-muted/30 text-sm">
              <div className="text-xs font-semibold text-muted-foreground mb-1">BILL SUMMARY</div>
              <div className="flex justify-between">
                <span>Lines selected</span>
                <span className="font-medium">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Retainage ({retainagePct}%)</span>
                <span>−{fmt(retainageAmount)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Bill Total</span>
                <span>{fmt(total)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="border-t shrink-0">
          {qbConnected && (
            <div className="px-6 pt-3 pb-1 flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={qbBusy}
                onClick={handleOpenInQB}
              >
                {qbBusy
                  ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Creating draft…</>
                  : qbDraftCreated
                    ? <><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Reopen QuickBooks draft</>
                    : <><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Open in QuickBooks{selectedIds.size > 0 ? ` (${selectedIds.size} lines)` : ""}</>
                }
              </Button>
              <span className="text-xs text-muted-foreground">
                {qbDraftCreated
                  ? "Draft created — click to reopen, then save in QuickBooks to sync back."
                  : selectedIds.size > 0
                    ? "Lines will be pre-filled — save in QuickBooks to sync back."
                    : "Select lines above to pre-fill the QB bill."}
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2 px-6 py-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleCreateBill} disabled={busy || selectedIds.size === 0}>
              {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Creating…</> : `Create Bill (${selectedIds.size} lines) →`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
